import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Shared Field Row Component
function DSFieldRow({ label, value }) {
  const theme = useTheme();
  const isEmpty =
    value === undefined || value === null || String(value).trim() === "";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "180px 1fr",
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.6,
          backgroundColor: `${theme.palette.primary.main}0d`,
          borderRight: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            lineHeight: 1.5,
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box
        sx={{
          px: 1.5,
          py: 0.6,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: isEmpty
              ? theme.palette.text.disabled
              : theme.palette.text.primary,
            fontStyle: isEmpty ? "italic" : "normal",
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {isEmpty ? "—" : String(value)}
        </Typography>
      </Box>
    </Box>
  );
}

// Shared Section Block Component
function DSSectionBlock({ title, fields, index }) {
  const theme = useTheme();
  if (!fields || fields.length === 0) return null;

  return (
    <Box>
      <Box
        sx={{
          px: 1.5,
          py: 0.6,
          backgroundColor: `${theme.palette.primary.main}18`,
          borderTop:
            index === 0 ? "none" : `1px solid ${theme.palette.primary.main}55`,
          borderBottom: `1px solid ${theme.palette.primary.main}55`,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <Box
          sx={{
            width: 3,
            height: 12,
            borderRadius: 1,
            backgroundColor: theme.palette.primary.main,
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            color: theme.palette.primary.main,
            textTransform: "uppercase",
            letterSpacing: "0.07em",
          }}
        >
          {title}
        </Typography>
      </Box>

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderTop: "none",
          mx: 1.5,
          borderRadius: "0 0 4px 4px",
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {fields.map(({ label, value }) => (
          <DSFieldRow key={label} label={label} value={value} />
        ))}
      </Box>

      <Box sx={{ height: 10 }} />
    </Box>
  );
}

/**
 * Unified renderer for both valve and instrument data sheets.
 * categorizedFields shape:  { [sectionTitle]: { [fieldLabel]: value } }
 */
export function CategorizedDataSheetContent({ categorizedFields }) {
  const sections = Object.entries(categorizedFields ?? {});

  if (sections.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
          No data sheet fields available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {sections.map(([title, fields], index) => {
        // fields is { label: value } — convert to array for DSSectionBlock
        const fieldEntries = Object.entries(fields ?? {}).map(
          ([label, value]) => ({ label, value }),
        );

        return (
          <DSSectionBlock
            key={title}
            title={title}
            fields={fieldEntries}
            index={index}
          />
        );
      })}
    </Box>
  );
}

// Main Dialog Component
export function DataSheetsDialog({ open, onClose, data }) {
  const theme = useTheme();
  const items = data?.items ?? [];
  const [selectedIndex, setSelectedIndex] = useState(0);

  const currentSheet = items[selectedIndex] ?? null;

  const getTabDisplayName = (tabKey) => {
    switch (tabKey) {
      case "trim":
        return "Trim List";
      case "bulk":
        return "PnID Bulk List";
      case "valve":
        return "Valve List";
      case "instrument":
        return "Instrument Index";
      case "equipment":
        return "Equipment List";
      default:
        return "Table";
    }
  };

  const renderContent = () => {
    if (!currentSheet) return null;

    return (
      <CategorizedDataSheetContent
        categorizedFields={currentSheet.categorizedFields}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: `1px solid ${theme.palette.primary.main}55`,
          maxWidth: 600,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1.25,
          px: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {getTabDisplayName(data?.tab)} — Data Sheets
            </Typography>
            {currentSheet && (
              <Typography
                sx={{ fontSize: "0.7rem", color: theme.palette.text.secondary }}
              >
                {currentSheet.tagNumbers?.length > 0
                  ? `Tags: ${currentSheet.tagNumbers.join(", ")}`
                  : "No tag numbers"}
                {" · "}
                {currentSheet.count} row{currentSheet.count !== 1 ? "s" : ""}
              </Typography>
            )}
          </Box>
        </Box>

        {items.length > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{ fontSize: "0.7rem", color: theme.palette.text.secondary }}
            >
              {selectedIndex + 1} / {items.length}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={selectedIndex === 0}
              onClick={() => setSelectedIndex((i) => i - 1)}
              sx={{
                minWidth: 28,
                px: 0.5,
                fontSize: "0.7rem",
                borderColor: `${theme.palette.primary.main}55`,
                color: theme.palette.primary.main,
              }}
            >
              ‹
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={selectedIndex === items.length - 1}
              onClick={() => setSelectedIndex((i) => i + 1)}
              sx={{
                minWidth: 28,
                px: 0.5,
                fontSize: "0.7rem",
                borderColor: `${theme.palette.primary.main}55`,
                color: theme.palette.primary.main,
              }}
            >
              ›
            </Button>
          </Box>
        )}
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, backgroundColor: theme.palette.background.default }}
      >
        {!items.length ? (
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{ fontSize: "0.8rem", color: theme.palette.text.secondary }}
            >
              No data sheets found for this tab.
            </Typography>
          </Box>
        ) : (
          renderContent()
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderColor: `${theme.palette.primary.main}55`,
            color: theme.palette.primary.main,
            fontSize: "0.72rem",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: `${theme.palette.primary.main}18`,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
